import React, { useEffect, useState, useRef } from 'react';
import { databases, ID } from '../services/appwriteClient';
import { Query } from 'appwrite';
import { getStudents } from '../services/studentService';
import { Student, Transaction } from '../types';
import { FaPrint, FaEnvelope, FaTrash, FaSync } from 'react-icons/fa';
import Receipt from '../templates/Receipt';
import { sendMail } from '../services/emailService';
import toast from 'react-hot-toast';
import { getReceiptMail } from '../utils/MailUtil';
import { generateTransactionId } from '../utils/IDUtil';
import TuitionBalances from '../components/fees/TuitionBalances';
import DaycareBalances from '../components/fees/DaycareBalances';
import CabBalances from '../components/fees/CabBalances';
import { fetchTransactionsForStudent } from '../services/feeTransactionService';
import { BarChart as ReBarChart, Bar as ReBar, XAxis as ReXAxis, YAxis as ReYAxis, Tooltip as ReTooltip, ResponsiveContainer as ReResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLLECTION_ID = '6863f9b6001639720d6e'; // fee transactions
const DATABASE_ID = '682acd26001f69683cd8'; // Use the correct database ID if different

const FeesPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    student_id: '',
    type: 'tuition', // default to tuition
    amount: '',
    mode: '',
    notes: '',
    date: new Date().toISOString().slice(0, 10), // yyyy-mm-dd
  });
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentOptions, setStudentOptions] = useState<Student[]>([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentMap, setStudentMap] = useState<{ [id: string]: Student }>({});
  const [printPreview, setPrintPreview] = useState<null | Transaction>(null);
  const receiptRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'transactions' | 'tuition' | 'daycare' | 'cab'>('transactions');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Sync fees state
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncPreview, setSyncPreview] = useState<Array<{
    student: Student;
    tuitionDiff: number;
    daycareDiff: number;
    cabDiff: number;
    hasChanges: boolean;
  }>>([]);
  const [applyingSync, setApplyingSync] = useState(false);

  // Add chart state
  const [feeStats, setFeeStats] = useState({
    lastMonth: 0,
    thisMonth: 0,
    total: 0,
    tuition: { collected: 0, collectable: 0 },
    daycare: { collected: 0, collectable: 0 },
    cab: { collected: 0, collectable: 0 },
  });

  // Add monthly bar chart data
  const [monthlyData, setMonthlyData] = useState<{ month: string; amount: number }[]>([]);

  // Calculate fees for a student from transactions
  const calculateStudentFees = (studentTransactions: Transaction[]) => {
    const tuitionTransactions = studentTransactions.filter(txn => 
      !txn.type || txn.type === 'tuition'
    );
    const daycareTransactions = studentTransactions.filter(txn => 
      txn.type === 'daycare'
    );
    const cabTransactions = studentTransactions.filter(txn => 
      txn.type === 'cab'
    );
    
    const calculatedTuition = tuitionTransactions.reduce((total, txn) => 
      total + parseFloat(txn.amount || '0'), 0
    );
    const calculatedDaycare = daycareTransactions.reduce((total, txn) => 
      total + parseFloat(txn.amount || '0'), 0
    );
    const calculatedCab = cabTransactions.reduce((total, txn) => 
      total + parseFloat(txn.amount || '0'), 0
    );
    
    return { calculatedTuition, calculatedDaycare, calculatedCab };
  };

  // Sync student fees before transaction creation
  const syncStudentFeesBeforeTransaction = async (studentId: string) => {
    try {
      const student = studentMap[studentId];
      if (!student) return;

      // Fetch all transactions for this student
      const studentTransactions = await fetchTransactionsForStudent(studentId);
      const { calculatedTuition, calculatedDaycare, calculatedCab } = calculateStudentFees(studentTransactions);
      
      const updateData: any = {};
      let needsUpdate = false;

      // Check tuition fees
      const currentTuition = typeof student.paid_fees === 'number' 
        ? student.paid_fees 
        : parseFloat(student.paid_fees || '0');
      if (Math.abs(calculatedTuition - currentTuition) > 0.01) {
        updateData.paid_fees = calculatedTuition.toString();
        needsUpdate = true;
      }

      // Check daycare fees
      const currentDaycare = typeof (student as any).paid_day_care_fees === 'number' 
        ? (student as any).paid_day_care_fees 
        : parseFloat((student as any).paid_day_care_fees || '0');
      if (Math.abs(calculatedDaycare - currentDaycare) > 0.01) {
        updateData.paid_day_care_fees = calculatedDaycare.toString();
        needsUpdate = true;
      }

      // Check cab fees
      const currentCab = typeof (student as any).paid_cab_fees === 'number' 
        ? (student as any).paid_cab_fees 
        : parseFloat((student as any).paid_cab_fees || '0');
      if (Math.abs(calculatedCab - currentCab) > 0.01) {
        updateData.paid_cab_fees = calculatedCab.toString();
        needsUpdate = true;
      }

      if (needsUpdate) {
        await databases.updateDocument(
          DATABASE_ID,
          '682acd33000e34dd93c0', // students collection
          studentId,
          updateData
        );
        
        // Update the student map with new data
        setStudentMap(prev => ({
          ...prev,
          [studentId]: {
            ...prev[studentId],
            ...updateData
          }
        }));
        
        toast.success('Student fees synced before transaction');
      }

      return { calculatedTuition, calculatedDaycare, calculatedCab };
    } catch (error) {
      console.error('Error syncing student fees before transaction:', error);
      toast.error('Error syncing fees');
      return null;
    }
  };

  // Get max amount for transaction based on fee type using fetched transactions
  const getMaxAmountForTransaction = (studentId: string, feeType: string) => {
    const student = studentMap[studentId];
    if (!student) return 0;

    let totalFees = 0;
    let paidFees = 0;

    // Filter transactions for this student by type
    const studentTransactions = transactions.filter(txn => txn.student_id === studentId);

    switch (feeType) {
      case 'tuition':
        totalFees = parseFloat(student.total_fees || '0');
        const tuitionTransactions = studentTransactions.filter(txn => 
          !txn.type || txn.type === 'tuition'
        );
        paidFees = tuitionTransactions.reduce((total, txn) => 
          total + parseFloat(txn.amount || '0'), 0
        );
        break;
      case 'daycare':
        totalFees = parseFloat((student as any).total_day_care_fees || '0');
        const daycareTransactions = studentTransactions.filter(txn => 
          txn.type === 'daycare'
        );
        paidFees = daycareTransactions.reduce((total, txn) => 
          total + parseFloat(txn.amount || '0'), 0
        );
        break;
      case 'cab':
        totalFees = parseFloat((student as any).total_cab_fees || '0');
        const cabTransactions = studentTransactions.filter(txn => 
          txn.type === 'cab'
        );
        paidFees = cabTransactions.reduce((total, txn) => 
          total + parseFloat(txn.amount || '0'), 0
        );
        break;
      default:
        return 0;
    }

    return Math.max(0, totalFees - paidFees);
  };

  // Fetch all transactions in the system (for bulk sync)
  const fetchAllTransactions = async (): Promise<Transaction[]> => {
    const allTxns: Transaction[] = [];
    let offset = 0;
    const limit = 100;
    while (true) {
      const res = await databases.listDocuments(
        DATABASE_ID,
        COLLECTION_ID,
        [
          Query.limit(limit),
          Query.offset(offset)
        ]
      );
      allTxns.push(...(res.documents as unknown as Transaction[]));
      if (res.documents.length < limit) break;
      offset += limit;
    }
    return allTxns;
  };

  // Preview sync changes
  const previewSyncChanges = async () => {
    setSyncLoading(true);
    try {
      const students = await getStudents();
      // Fetch all transactions at once
      const allTransactions = await fetchAllTransactions();
      // Group transactions by student_id
      const transactionsByStudent: { [studentId: string]: Transaction[] } = {};
      for (const txn of allTransactions) {
        if (!txn.student_id) continue;
        if (!transactionsByStudent[txn.student_id]) transactionsByStudent[txn.student_id] = [];
        transactionsByStudent[txn.student_id].push(txn);
      }
      const preview = [];
      for (const student of students) {
        const studentTransactions = transactionsByStudent[student.id] || [];
        const { calculatedTuition, calculatedDaycare, calculatedCab } = calculateStudentFees(studentTransactions);
        const currentTuition = typeof student.paid_fees === 'number' 
          ? student.paid_fees 
          : parseFloat(student.paid_fees || '0');
        const currentDaycare = typeof (student as any).paid_day_care_fees === 'number' 
          ? (student as any).paid_day_care_fees 
          : parseFloat((student as any).paid_day_care_fees || '0');
        const currentCab = typeof (student as any).paid_cab_fees === 'number' 
          ? (student as any).paid_cab_fees 
          : parseFloat((student as any).paid_cab_fees || '0');
        const tuitionDiff = calculatedTuition - currentTuition;
        const daycareDiff = calculatedDaycare - currentDaycare;
        const cabDiff = calculatedCab - currentCab;
        const hasChanges = Math.abs(tuitionDiff) > 0.01 || Math.abs(daycareDiff) > 0.01 || Math.abs(cabDiff) > 0.01;
        preview.push({
          student,
          tuitionDiff,
          daycareDiff,
          cabDiff,
          hasChanges
        });
      }
      setSyncPreview(preview);
      setSyncDialogOpen(true);
    } catch (error) {
      console.error('Error previewing sync changes:', error);
      toast.error('Error previewing sync changes');
    }
    setSyncLoading(false);
  };

  // Apply sync changes
  const applySyncChanges = async () => {
    setApplyingSync(true);
    try {
      const studentsToUpdate = syncPreview.filter(item => item.hasChanges);
      
      for (const item of studentsToUpdate) {
        const updateData: any = {};
        
        if (Math.abs(item.tuitionDiff) > 0.01) {
          const newTuition = (typeof item.student.paid_fees === 'number' 
            ? item.student.paid_fees 
            : parseFloat(item.student.paid_fees || '0')) + item.tuitionDiff;
          updateData.paid_fees = newTuition.toString();
        }
        
        if (Math.abs(item.daycareDiff) > 0.01) {
          const newDaycare = (typeof (item.student as any).paid_day_care_fees === 'number' 
            ? (item.student as any).paid_day_care_fees 
            : parseFloat((item.student as any).paid_day_care_fees || '0')) + item.daycareDiff;
          updateData.paid_day_care_fees = newDaycare.toString();
        }
        
        if (Math.abs(item.cabDiff) > 0.01) {
          const newCab = (typeof (item.student as any).paid_cab_fees === 'number' 
            ? (item.student as any).paid_cab_fees 
            : parseFloat((item.student as any).paid_cab_fees || '0')) + item.cabDiff;
          updateData.paid_cab_fees = newCab.toString();
        }
        
        await databases.updateDocument(
          DATABASE_ID,
          '682acd33000e34dd93c0', // students collection
          item.student.id,
          updateData
        );
      }
      
      toast.success(`Successfully synced fees for ${studentsToUpdate.length} students`);
      setSyncDialogOpen(false);
      setSyncPreview([]);
      
      // Reload transactions and student data
      fetchTransactions();
      const updatedStudents = await getStudents();
      const updatedMap: { [id: string]: Student } = {};
      updatedStudents.forEach(s => { updatedMap[s.id] = s; });
      setStudentMap(updatedMap);
    } catch (error) {
      console.error('Error applying sync changes:', error);
      toast.error('Error applying sync changes');
    }
    setApplyingSync(false);
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let allTransactions: Transaction[] = [];
      let offset = 0;
      const limit = 100;
      
      while (true) {
        const res = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
          Query.limit(limit),
          Query.offset(offset)
        ]);
        allTransactions = [...allTransactions, ...(res.documents as unknown as Transaction[])];
        
        if (res.documents.length < limit) {
          break; // No more documents to fetch
        }
        offset += limit;
      }
      
      setTransactions(allTransactions as unknown as Transaction[]);
    } catch (err) {
      // handle error
    }
    setLoading(false);
    setDialogOpen(false);
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  // Fetch students for autocomplete
  useEffect(() => {
    if (studentSearch.length === 0) {
      setStudentOptions([]);
      return;
    }
    setStudentLoading(true);
    getStudents().then(students => {
      setStudentOptions(
        students.filter(s =>
          s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
          s.register_num.toLowerCase().includes(studentSearch.toLowerCase())
        )
      );
      setStudentLoading(false);
    });
  }, [studentSearch]);

  // Fetch all students and map by id for quick lookup
  useEffect(() => {
    getStudents().then(students => {
      const map: { [id: string]: Student } = {};
      students.forEach(s => { map[s.id] = s; });
      setStudentMap(map);
    });
  }, []);

  // Helper to calculate stats efficiently using fetched data
  useEffect(() => {
    if (Object.keys(studentMap).length === 0) return; // Wait for student data
    
    // Calculate collectable sums from studentMap
    let tuitionCollectable = 0, daycareCollectable = 0, cabCollectable = 0;
    Object.values(studentMap).forEach(s => {
      tuitionCollectable += parseFloat(s.total_fees || '0');
      daycareCollectable += parseFloat((s as any).total_day_care_fees || '0');
      cabCollectable += parseFloat((s as any).total_cab_fees || '0');
    });

    // Calculate collected sums and monthly data from transactions
    let tuitionCollected = 0, daycareCollected = 0, cabCollected = 0;
    let lastMonth = 0, thisMonth = 0, total = 0;
    const monthlyMap: { [month: string]: number } = {};
    
    const now = new Date();
    const thisMonthStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthStr = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth()+1).padStart(2,'0')}`;
    
    transactions.forEach(txn => {
      const amt = parseFloat(txn.amount || '0');
      total += amt;
      
      // Categorize by fee type
      if (txn.type === 'daycare') daycareCollected += amt;
      else if (txn.type === 'cab') cabCollected += amt;
      else tuitionCollected += amt;
      
      // Monthly calculations
      const txnDate = txn.time ? txn.time.slice(0,7) : '';
      if (txnDate) {
        monthlyMap[txnDate] = (monthlyMap[txnDate] || 0) + amt;
        if (txnDate === thisMonthStr) thisMonth += amt;
        if (txnDate === lastMonthStr) lastMonth += amt;
      }
    });

    // Update fee stats
    setFeeStats({
      lastMonth, thisMonth, total,
      tuition: { collected: tuitionCollected, collectable: tuitionCollectable },
      daycare: { collected: daycareCollected, collectable: daycareCollectable },
      cab: { collected: cabCollected, collectable: cabCollectable },
    });

    // Update monthly chart data
    const monthlyArr = Object.entries(monthlyMap)
      .map(([month, amount]) => ({ month, amount }))
      .sort((a, b) => a.month.localeCompare(b.month));
    setMonthlyData(monthlyArr);
  }, [transactions, studentMap]);

  // Calculate percentage diff for this month vs last month
  const monthDiff = feeStats.lastMonth === 0 ? 0 : ((feeStats.thisMonth - feeStats.lastMonth) / feeStats.lastMonth) * 100;
  const monthDiffColor = monthDiff > 0 ? 'text-green-600' : monthDiff < 0 ? 'text-red-600' : 'text-gray-500';
  const monthDiffSign = monthDiff > 0 ? '+' : '';

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Sync fees when fee type changes and student is selected
    if (name === 'type' && form.student_id) {
      await syncStudentFeesBeforeTransaction(form.student_id);
    }
  };

  // Sync fees when dialog opens
  const handleDialogOpen = async () => {
    setDialogOpen(true);
    // Reset form when opening dialog
    setForm({ student_id: '', type: 'tuition', amount: '', mode: '', notes: '', date: new Date().toISOString().slice(0, 10) });
    setStudentSearch('');
  };

  // Sync fees when student is selected in dialog
  const handleStudentSelect = async (student: Student) => {
    setForm(f => ({ ...f, student_id: student.id }));
    setStudentSearch(student.name + ' (' + student.register_num + ')');
    setStudentOptions([]);
    // Sync fees for the selected student using transactions from the collection
    await syncStudentFeesBeforeTransaction(student.id);
  };

  const handleDeleteTransaction = async () => {
    if (!transactionToDelete) return;
    
    setDeleting(true);
    try {
      // 1. Delete the transaction document
      await databases.deleteDocument(
        DATABASE_ID,
        COLLECTION_ID,
        transactionToDelete.$id
      );

      // 2. Update the student document to remove the transaction ID and adjust paid_fees
      if (transactionToDelete.student_id) {
        const studentDoc = await databases.getDocument(
          DATABASE_ID,
          '682acd33000e34dd93c0',
          transactionToDelete.student_id
        );

        // Remove the transaction ID from the student's transactions array
        const updatedTransactions = (studentDoc.transactions as string[]).filter(
          id => id !== transactionToDelete.$id
        );

        // Determine which paid fees field to update based on transaction type
        const txnType = transactionToDelete.type || 'tuition';
        let paidField = 'paid_fees';
        let currentPaid = 0;
        switch (txnType) {
          case 'daycare':
            paidField = 'paid_day_care_fees';
            currentPaid = typeof studentDoc.paid_day_care_fees === 'number'
              ? studentDoc.paid_day_care_fees
              : parseFloat(studentDoc.paid_day_care_fees || '0');
            break;
          case 'cab':
            paidField = 'paid_cab_fees';
            currentPaid = typeof studentDoc.paid_cab_fees === 'number'
              ? studentDoc.paid_cab_fees
              : parseFloat(studentDoc.paid_cab_fees || '0');
            break;
          case 'tuition':
          default:
            paidField = 'paid_fees';
            currentPaid = typeof studentDoc.paid_fees === 'number'
              ? studentDoc.paid_fees
              : parseFloat(studentDoc.paid_fees || '0');
            break;
        }

        const deletedAmount = parseFloat(transactionToDelete.amount || '0');
        const newPaid = Math.max(0, currentPaid - deletedAmount);

        // Update the student document with the correct paid field
        await databases.updateDocument(
          DATABASE_ID,
          '682acd33000e34dd93c0',
          transactionToDelete.student_id,
          {
            [paidField]: newPaid.toString(),
            transactions: updatedTransactions,
          }
        );
      }

      // 3. Refresh the transactions list
      fetchTransactions();
      
      // 5. Close the dialog and reset state
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
    } catch (err) {
      console.error('Error deleting transaction:', err);
      // You might want to show an error message to the user here
    }
    setDeleting(false);
  };

  const openDeleteDialog = (transaction: Transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Sync student fees before creating transaction
      const syncedFees = await syncStudentFeesBeforeTransaction(form.student_id);
      if (!syncedFees) {
        toast.error('Failed to sync student fees');
        setSubmitting(false);
        return;
      }

      // Get the current calculated paid fees after sync
      let currentPaidFees = 0;
      switch(form.type) {
        case "daycare":
          currentPaidFees = syncedFees.calculatedDaycare;
          break;
        case "cab":
          currentPaidFees = syncedFees.calculatedCab;
          break;
        case "tuition":
        default:
          currentPaidFees = syncedFees.calculatedTuition;
          break;
      }

      const txnAmount = parseFloat(form.amount || '0');
      const newPaidFees = currentPaidFees + txnAmount;

      // Get total fees for validation
      const student = studentMap[form.student_id];
      let totalFees = 0;
      switch(form.type) {
        case "daycare":
          totalFees = parseFloat((student as any).total_day_care_fees || '0');
          break;
        case "cab":
          totalFees = parseFloat((student as any).total_cab_fees || '0');
          break;
        case "tuition":
        default:
          totalFees = parseFloat(student.total_fees || '0');
          break;
      }

      if (newPaidFees > totalFees) {
        toast.error("Amount exceeds total payable fees");
        setSubmitting(false);
        return;
      }

      if (txnAmount <= 0) {
        toast.error("Amount must be greater than 0");
        setSubmitting(false);
        return;
      }

      const generatedTransactionId = generateTransactionId(form.student_id, form.date, form.type);
      
      await databases.createDocument(
        DATABASE_ID,
        COLLECTION_ID,
        ID.unique(),
        {
          student_id: form.student_id,
          type: form.type,
          amount: form.amount,
          mode: form.mode,
          time: new Date(form.date).toISOString(), // convert to ISO
          notes: form.notes,
          transaction_id: generatedTransactionId,
          paid: currentPaidFees.toString()
        }
      );
      
      // Update student document with updated paid fees (do NOT update transactions array)
      var updateData = {}

      switch(form.type) {
        case "daycare":
          updateData = {
            paid_day_care_fees: newPaidFees.toString()
          }
          break;
        case "cab":
          updateData = {
            paid_cab_fees: newPaidFees.toString()
          }          
          break;
        default:
        case "tuition":
          updateData = {
            paid_fees: newPaidFees.toString()
          }          
          break;
      }
      
      await databases.updateDocument(
        DATABASE_ID,
        '682acd33000e34dd93c0',
        form.student_id,
        updateData
      );
      
      // Reload all transactions after successful add
      fetchTransactions();
      setForm({ student_id: '', type: 'tuition', amount: '', mode: '', notes: '', date: new Date().toISOString().slice(0, 10) });
      setStudentSearch('');
      setDialogOpen(false);
      toast.success('Transaction created successfully');
    } catch (err) {
      console.error('Error creating transaction:', err);
      toast.error('Failed to create transaction');
    }
    setSubmitting(false);
  };

  // Sort transactions from newest to oldest
  const filteredTransactions = transactions
    .filter(txn =>
      txn.amount.includes(search) ||
      txn.mode.toLowerCase().includes(search.toLowerCase()) ||
      (txn.notes && txn.notes.toLowerCase().includes(search.toLowerCase())) ||
      (txn.transaction_id && txn.transaction_id.toLowerCase().includes(search.toLowerCase()))
    )
    .sort((a, b) => {
      // Use Appwrite system field $createdAt for sorting
      const aDate = new Date((a as any).$createdAt);
      const bDate = new Date((b as any).$createdAt);
      return bDate.getTime() - aDate.getTime();
    });

  // Pie chart colors
  const PIE_COLORS = ['#10b981', '#fbbf24']; // collected, remaining

  return (
    <div className="container mx-auto px-4 py-8">
       <div className="mb-8">
            {/* Top-level summary bar chart */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded shadow p-4 flex flex-col items-center">
                <div className="text-gray-500 text-sm mb-1">Fees Collected Last Month</div>
                <div className="text-2xl font-bold text-pink-600">₹{feeStats.lastMonth.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded shadow p-4 flex flex-col items-center">
                <div className="text-gray-500 text-sm mb-1">Fees Collected This Month</div>
                <div className="text-2xl font-bold text-blue-600">₹{feeStats.thisMonth.toLocaleString()}</div>
                <div className={`text-xs mt-1 ${monthDiffColor}`}>{monthDiffSign}{monthDiff.toFixed(1)}% from last month</div>
              </div>
              <div className="bg-white rounded shadow p-4 flex flex-col items-center">
                <div className="text-gray-500 text-sm mb-1">Total Fees Collected</div>
                <div className="text-2xl font-bold text-green-600">₹{feeStats.total.toLocaleString()}</div>
              </div>
            </div>
            {/* Monthly collection bar chart */}
            <div className="mb-8 bg-white rounded shadow p-4">
              <div className="font-semibold mb-2">Monthly Collection Trend</div>
              <ReResponsiveContainer width="100%" height={220}>
                <ReBarChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <ReXAxis dataKey="month" />
                  <ReYAxis />
                  <ReTooltip />
                  <Legend />
                  <ReBar dataKey="amount" fill="#6366f1" name="Collected" />
                </ReBarChart>
              </ReResponsiveContainer>
            </div>
            {/* Category breakdowns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Tuition Pie */}
              <div className="bg-white rounded shadow p-4 flex flex-col items-center">
                <div className="font-semibold mb-2">Tuition Fees</div>
                <ReResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={[{ name: 'Collected', value: feeStats.tuition.collected }, { name: 'Remaining', value: Math.max(0, feeStats.tuition.collectable - feeStats.tuition.collected) }]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      fill="#10b981"
                      label={({ name, percent }: { name: string; percent?: number }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                    >
                      <Cell key="collected" fill={PIE_COLORS[0]} />
                      <Cell key="remaining" fill={PIE_COLORS[1]} />
                    </Pie>
                    <Legend />
                  </PieChart>
                </ReResponsiveContainer>
                <div className="text-xs text-gray-500 mt-2">Collected: ₹{feeStats.tuition.collected.toLocaleString()} / Collectable: ₹{feeStats.tuition.collectable.toLocaleString()}</div>
              </div>
              {/* Daycare Pie */}
              <div className="bg-white rounded shadow p-4 flex flex-col items-center">
                <div className="font-semibold mb-2">Daycare Fees</div>
                <ReResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={[{ name: 'Collected', value: feeStats.daycare.collected }, { name: 'Remaining', value: Math.max(0, feeStats.daycare.collectable - feeStats.daycare.collected) }]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      fill="#f472b6"
                      label={({ name, percent }: { name: string; percent?: number }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                    >
                      <Cell key="collected" fill="#f472b6" />
                      <Cell key="remaining" fill={PIE_COLORS[1]} />
                    </Pie>
                    <Legend />
                  </PieChart>
                </ReResponsiveContainer>
                <div className="text-xs text-gray-500 mt-2">Collected: ₹{feeStats.daycare.collected.toLocaleString()} / Collectable: ₹{feeStats.daycare.collectable.toLocaleString()}</div>
              </div>
              {/* Cab Pie */}
              <div className="bg-white rounded shadow p-4 flex flex-col items-center">
                <div className="font-semibold mb-2">Cab Fees</div>
                <ReResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={[{ name: 'Collected', value: feeStats.cab.collected }, { name: 'Remaining', value: Math.max(0, feeStats.cab.collectable - feeStats.cab.collected) }]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      fill="#f59e42"
                      label={({ name, percent }: { name: string; percent?: number }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                    >
                      <Cell key="collected" fill="#f59e42" />
                      <Cell key="remaining" fill={PIE_COLORS[1]} />
                    </Pie>
                    <Legend />
                  </PieChart>
                </ReResponsiveContainer>
                <div className="text-xs text-gray-500 mt-2">Collected: ₹{feeStats.cab.collected.toLocaleString()} / Collectable: ₹{feeStats.cab.collectable.toLocaleString()}</div>
              </div>
            </div>
          </div>
      {/* Tabs */}
      <div className="flex mb-6 border-b">
        <button
          className={`px-6 py-2 font-semibold ${activeTab === 'transactions' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('transactions')}
        >
          Transactions
        </button>
        <button
          className={`px-6 py-2 font-semibold ${activeTab === 'tuition' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('tuition')}
        >
          Tuition Balance
        </button>
        <button
          className={`px-6 py-2 font-semibold ${activeTab === 'daycare' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('daycare')}
        >
          Daycare Balance
        </button>
        <button
          className={`px-6 py-2 font-semibold ${activeTab === 'cab' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('cab')}
        >
          Cab Balance
        </button>
      </div>
      {/* Tab Content */}
      {activeTab === 'transactions' && (
        <>
          <div className="mb-6 flex justify-between items-center">
            <button
              className="bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700"
              onClick={handleDialogOpen}
            >
              New Transaction
            </button>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
              onClick={previewSyncChanges}
              disabled={syncLoading}
            >
              <FaSync className={syncLoading ? 'animate-spin' : ''} />
              {syncLoading ? 'Analyzing...' : 'Sync Fees Information'}
            </button>
          </div>
          {/* Dialog for creating new transaction */}
          {dialogOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <div className="bg-white p-6 rounded shadow-md max-w-xl w-full relative">
                <button
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                  onClick={() => setDialogOpen(false)}
                >
                  &times;
                </button>
                <h2 className="text-lg font-semibold mb-4">Create New Transaction</h2>
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Student</label>
                    <input
                      type="text"
                      placeholder="Search student by name or reg. number..."
                      value={studentSearch}
                      onChange={e => setStudentSearch(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      autoComplete="off"
                    />
                    {studentLoading && <div className="text-xs text-gray-400 mt-1">Searching...</div>}
                    {studentOptions.length > 0 && (
                      <ul className="border rounded bg-white mt-1 max-h-40 overflow-y-auto z-10">
                        {studentOptions.map(student => (
                          <li
                            key={student.id}
                            className={`px-3 py-2 cursor-pointer hover:bg-pink-100 ${form.student_id === student.id ? 'bg-pink-50' : ''}`}
                            onClick={() => handleStudentSelect(student)}
                          >
                            {student.name} ({student.register_num})
                          </li>
                        ))}
                      </ul>
                    )}
                    {form.student_id && (
                      <div className="text-xs text-green-600 mt-1">Linked to student ID: {form.student_id}</div>
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Type</label>
                    <select
                      name="type"
                      value={form.type}
                      onChange={handleInputChange}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="tuition">Tuition</option>
                      <option value="daycare">Daycare</option>
                      <option value="cab">Cab</option>
                    </select>
                  </div>
                    
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Amount</label>
                    <input
                      type="text"
                      name="amount"
                      value={form.amount}
                      onChange={handleInputChange}
                      className="w-full border rounded px-3 py-2"
                      required
                      placeholder={`Max: ${form.student_id ? getMaxAmountForTransaction(form.student_id, form.type) : 0}`}
                    />
                    {form.student_id && (
                      <div className="text-xs text-gray-500 mt-1">
                        Max payable: ₹{getMaxAmountForTransaction(form.student_id, form.type)}
                      </div>
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Mode</label>
                    <input
                      type="text"
                      name="mode"
                      value={form.mode}
                      onChange={handleInputChange}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handleInputChange}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Date</label>
                    <input
                      type="date"
                      name="date"
                      value={form.date}
                      onChange={handleInputChange}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <button
                    onClick={() => toast.success('Transaction created successfully')}
                    type="submit"
                    className="bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700"
                    disabled={submitting}
                  >
                    {submitting ? 'Creating...' : 'Create Transaction'}
                  </button>
                </form>
              </div>
            </div>
          )}
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-lg font-semibold">All Transactions</h2>
            <input
              type="text"
              placeholder="Search transactions..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border rounded px-3 py-2 w-64"
            />
          </div>
          <div className="overflow-x-auto bg-white rounded shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Roll No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-8">Loading...</td></tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-8">No transactions found.</td></tr>
                ) : (
                  filteredTransactions.map(txn => {
                    // Determine row background color based on type
                    let rowBg = '';
                    switch (txn.type) {
                      case 'tuition':
                        rowBg = 'bg-green-50';
                        break;
                      case 'daycare':
                        rowBg = 'bg-pink-50';
                        break;
                      case 'cab':
                        rowBg = 'bg-yellow-50';
                        break;
                      default:
                        rowBg = 'bg-green-50';
                    }
                    // Format date as dd-mm-yy
                    const dateObj = new Date(txn.time);
                    const dateStr = `${dateObj.getDate().toString().padStart(2, '0')}-${(dateObj.getMonth()+1).toString().padStart(2, '0')}-${dateObj.getFullYear().toString().slice(-2)}`;
                    // Type label
                    let typeLabel = '-';
                    switch (txn.type) {
                      case 'tuition':
                        typeLabel = 'Tuition';
                        break;
                      case 'daycare':
                        typeLabel = 'Daycare';
                        break;
                      case 'cab':
                        typeLabel = 'Cab';
                        break;
                      default:
                        typeLabel = '-';
                    }
                    return (
                      <tr key={txn.$id} className={rowBg}>
                        <td className="px-6 py-4 whitespace-nowrap">{dateStr}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{typeLabel}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{txn.mode}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{txn.transaction_id}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{txn.student_id ? (studentMap[txn.student_id]?.register_num || '-') : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{txn.student_id ? (studentMap[txn.student_id]?.name || '-') : '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{txn.notes || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-bold">{txn.amount}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button className="text-pink-600 hover:text-pink-800" onClick={() => setPrintPreview(txn)}>
                              <FaPrint />
                            </button>
                            <button className="text-red-600 hover:text-red-800" onClick={() => openDeleteDialog(txn)}>
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Print Preview Modal */}
          {printPreview && (
            <div style={{ minHeight: '90vh', minWidth: '90vw' }} className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50 overflow-auto">
              <div className="bg-white p-8 rounded shadow-md max-w-5xl w-full relative overflow-auto" style={{ maxHeight: '95vh' }}>
                <button
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                  onClick={() => setPrintPreview(null)}
                >
                  &times;
                </button>
                {/* Calculate balance: total_fees - paid_fees for the student */}
                {(() => {
                  let balance = '0';
                  let rollNum = '-';

                  if (printPreview.student_id && studentMap[printPreview.student_id]) {
                    const student = studentMap[printPreview.student_id];

                    let total = 0;
                    let paid = printPreview.paid ? parseFloat(printPreview.paid) : 0;
                    
                    switch (printPreview.type) {
                      case 'daycare':
                        total = parseFloat(student.total_day_care_fees || '0');
                        break;
                      case 'cab':
                        total = parseFloat(student.total_cab_fees || '0');
                        break;
                      default:
                      case 'tuition':
                        total = parseFloat(student.total_fees || '0');
                        break;
                    }
                    const txnAmount = parseFloat(printPreview.amount || '0');
                    balance = (total - (paid + txnAmount)).toString();
                    rollNum = student.register_num || '-';
                  }
                  return (
                    <Receipt
                      ref={receiptRef}
                      amount={printPreview.amount}
                      transactionId={printPreview.transaction_id || printPreview.$id}
                      balance={balance}
                      studentInfo={printPreview.student_id ? (studentMap[printPreview.student_id]?.name || '-') : '-'}
                      studentId={rollNum}
                      receiptId={printPreview.$id}
                      schoolName="Euro Wis"
                      type={['tuition', 'daycare', 'cab'].includes(printPreview.type as string) ? printPreview.type as 'tuition' | 'daycare' | 'cab' : 'tuition'}
                      parentName={printPreview.student_id ? studentMap[printPreview.student_id]?.fathers_name || '-' : '-'}
                      transactionDate={printPreview.time}
                    />
                  );
                })()}
                <div className="flex justify-end mt-4 gap-2">
                <button
                    className="ml-2 flex items-center gap-2 border border-pink-600 text-pink-600 px-4 py-2 rounded hover:bg-pink-50"
                    onClick={async () => {
                      if (printPreview && printPreview.student_id) {
                        const email = studentMap[printPreview.student_id]?.email;
                        if (!email) {
                          alert('No parent email found!');
                          return;
                        }
                        let balance = '0';
                        let rollNum = '-';
                        if (printPreview.student_id && studentMap[printPreview.student_id]) {
                          const student = studentMap[printPreview.student_id];
                          let total = 0;
                          let paid = 0;
                          switch (printPreview.type) {
                            case 'tuition':
                              total = parseFloat(student.total_fees || '0');
                              paid = printPreview.paid ? parseFloat(printPreview.paid) : 0;
                              break;
                            case 'daycare':
                              total = parseFloat(student.total_day_care_fees || '0');
                              paid = printPreview.paid ? parseFloat(printPreview.paid) : 0;
                              break;
                            case 'cab':
                              total = parseFloat(student.total_cab_fees || '0');
                              paid = printPreview.paid ? parseFloat(printPreview.paid) : 0;
                              break;
                          }
                          const txnAmount = parseFloat(printPreview.amount || '0');
                          balance = (total - (paid + txnAmount)).toString();
                          rollNum = student.register_num || '-';
                        }
                        const html = getReceiptMail({
                          amount: printPreview.amount,
                          transactionId: printPreview.transaction_id || printPreview.$id,
                          balance,
                          studentInfo: printPreview.student_id ? (studentMap[printPreview.student_id]?.name || '-') : '-',
                          studentId: rollNum,
                          receiptId: printPreview.$id,
                          schoolName: 'Euro Wis Kids Preschool',
                        });
                        try { 
                          await sendMail({
                            to: email,
                            html,
                            subject: "Fees Receipt"
                          });
                          toast.success('Receipt emailed to parent!');
                        } catch (err: any) {
                          toast.error('Failed to send email: ' + (err.message || err));
                        }
                      }
                    }}
                  >
                    <FaEnvelope />
                    Mail Receipt
                  </button>
                  <button
                    className="flex items-center gap-2 bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700"
                    onClick={() => {
                      if (receiptRef.current) {
                        const printContents = receiptRef.current.innerHTML;
                        const printWindow = window.open('', '', 'height=900,width=1200');
                        if (printWindow) {
                          printWindow.document.write(`<!DOCTYPE html><html><head><title>Receipt</title>` +
                            `<link href='https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css' rel='stylesheet'>` +
                            `</head><body class='bg-white'>` +
                            printContents +
                            `</body></html>`);
                          printWindow.document.close();
                          printWindow.focus();
                          setTimeout(() => printWindow.print(), 500);
                        }
                      }
                    }}
                  >
                    <FaPrint />
                    Print / Download
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

        {/* Delete Confirmation Dialog */}
        {deleteDialogOpen && transactionToDelete && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <div className="bg-white p-6 rounded shadow-md max-w-md w-full relative">
                <h2 className="text-lg font-semibold mb-4 text-red-600">Delete Transaction</h2>
                <div className="mb-4">
                  <p className="text-gray-700 mb-2">
                    <strong>Warning:</strong> This action is irreversible and will permanently delete this transaction.
                  </p>
                  <div className="bg-gray-50 p-3 rounded">
                    <p><strong>Transaction Details:</strong></p>
                    <p>Amount: ₹{transactionToDelete.amount}</p>
                    <p>Mode: {transactionToDelete.mode}</p>
                    <p>Time: {new Date(transactionToDelete.time).toLocaleString()}</p>
                    <p>Student: {transactionToDelete.student_id ? (studentMap[transactionToDelete.student_id]?.name || 'Unknown') : 'Unknown'}</p>
                    <p>Roll No: {transactionToDelete.student_id ? (studentMap[transactionToDelete.student_id]?.register_num || 'Unknown') : 'Unknown'}</p>
                    {transactionToDelete.notes && <p>Notes: {transactionToDelete.notes}</p>}
                    {transactionToDelete.transaction_id && <p>Receipt ID: {transactionToDelete.transaction_id}</p>}
                  </div>
                  <p className="text-red-600 mt-2">
                    <strong>Impact:</strong> This will also update the student's paid fees and remove this transaction from their record.
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                    onClick={() => {
                      setDeleteDialogOpen(false);
                      setTransactionToDelete(null);
                    }}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    onClick={handleDeleteTransaction}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete Transaction'}
                  </button>
                </div>
              </div>
            </div>
          )}

      {activeTab === 'tuition' && <TuitionBalances />}
      {activeTab === 'daycare' && <DaycareBalances />}
      {activeTab === 'cab' && <CabBalances />}

      {/* Sync Preview Dialog */}
      {syncDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded shadow-md max-w-6xl w-full max-h-[90vh] overflow-auto relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={() => setSyncDialogOpen(false)}
            >
              &times;
            </button>
            <h2 className="text-lg font-semibold mb-4 text-blue-600">Sync Fees Information - Preview</h2>
            
            {syncPreview.filter(item => item.hasChanges).length === 0 ? (
              <div className="text-center py-8">
                <div className="mb-4">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-green-800 mb-2">No Issues Found</h3>
                  <p className="text-gray-600">All student fees are up to date and synchronized with their transaction records.</p>
                </div>
                <div className="mt-6">
                  <button
                    className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    onClick={() => setSyncDialogOpen(false)}
                  >
                    Okay
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-yellow-800">
                    <strong>Preview:</strong> The following students have fee discrepancies that will be corrected.
                  </p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roll No</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tuition Difference</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Daycare Difference</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cab Difference</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {syncPreview
                        .filter(item => item.hasChanges)
                        .map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">{item.student.name}</td>
                            <td className="px-4 py-3 whitespace-nowrap">{item.student.register_num}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {Math.abs(item.tuitionDiff) > 0.01 ? (
                                <span className={item.tuitionDiff > 0 ? 'text-green-600' : 'text-red-600'}>
                                  {item.tuitionDiff > 0 ? '+' : ''}{item.tuitionDiff.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-gray-400">No change</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {Math.abs(item.daycareDiff) > 0.01 ? (
                                <span className={item.daycareDiff > 0 ? 'text-green-600' : 'text-red-600'}>
                                  {item.daycareDiff > 0 ? '+' : ''}{item.daycareDiff.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-gray-400">No change</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {Math.abs(item.cabDiff) > 0.01 ? (
                                <span className={item.cabDiff > 0 ? 'text-green-600' : 'text-red-600'}>
                                  {item.cabDiff > 0 ? '+' : ''}{item.cabDiff.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-gray-400">No change</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                                Will Update
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                    onClick={() => setSyncDialogOpen(false)}
                    disabled={applyingSync}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    onClick={applySyncChanges}
                    disabled={applyingSync}
                  >
                    {applyingSync ? 'Applying Changes...' : `Apply Changes (${syncPreview.filter(item => item.hasChanges).length} students)`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeesPage;